import { UpdateMediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/actions';
import { normalizeTemplateName } from 'modules/manager/domain/template-name-conflict';
import { TemplateFileEntry } from './template-file';

export interface ExistingTemplateRef {
  id: number;
  name: string;
}

export interface NameConflict {
  /** Position in the `entries` passed in. */
  entryIndex: number;
  existingId: number;
  /** Name as it is in the target list. */
  existingName: string;
}

/**
 * Entries whose name is already taken in the target list, compared like the DB
 * index. When the file repeats a name, only its first entry conflicts: after it
 * is overwritten, the rest go the default way (suffix or skip).
 */
export function findNameConflicts(
  entries: readonly Pick<TemplateFileEntry, 'name'>[],
  existing: readonly ExistingTemplateRef[],
): NameConflict[] {
  const byName = new Map<string, ExistingTemplateRef>();
  existing.forEach((template) => {
    const key = normalizeTemplateName(template.name);
    if (!byName.has(key)) byName.set(key, template);
  });

  const seen = new Set<string>();
  const conflicts: NameConflict[] = [];
  entries.forEach((entry, entryIndex) => {
    const key = normalizeTemplateName(entry.name);
    if (seen.has(key)) return;
    seen.add(key);
    const match = byName.get(key);
    if (match) conflicts.push({ entryIndex, existingId: match.id, existingName: match.name });
  });
  return conflicts;
}

/**
 * Update that replaces a template's content with the file entry. `name` and
 * `order` stay: the name already matches and the template keeps its place.
 */
export function toTemplateOverwrite(entry: TemplateFileEntry, id: number): UpdateMediaManagerTemplate {
  return {
    id,
    objects: entry.objects,
    version: entry.version,
    sportType: entry.sportType,
    width: entry.width,
    height: entry.height,
    templateType: entry.templateType,
    templateSubtypes: entry.templateSubtypes,
    matchLayout: entry.matchLayout,
    playerRanking: entry.playerRanking,
  };
}
